import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';

import { RootState } from '../..';
import {
  createCourseStudent,
  deleteCourseStudent,
  getCourseStudentsAsAdmin,
  updateCourseStudent,
} from './adminApi';
import { IBaseUser } from '../../UserSlice';

export interface ICourseStudentForAdmin {
  _id: string;
  status: 'ENABLE' | 'DISABLE';
  startDate: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
  student: IBaseUser;
}

export interface IInitialCourseStudentsState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminCourseStudentsData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICourseStudentForAdmin>;
  };
}

const initialState: IInitialCourseStudentsState = {
  status: 'idle',
  // Admin
  adminCourseStudentsData: undefined,
};

export const CourseStudentsSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    resetAdminCourseStudentsData: (state) => {
      state.adminCourseStudentsData = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // Admin
      // Get course students
      .addCase(getCourseStudentsAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;
        const newAdminCourseStudentData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminCourseStudentsData = newAdminCourseStudentData;
      })
      // Create course students
      .addCase(createCourseStudent.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        if (state.adminCourseStudentsData) {
          const newAdminCourseStudentsData = {
            ...state.adminCourseStudentsData,
          };

          newAdminCourseStudentsData.list.unshift(result);

          if (newAdminCourseStudentsData.totalDocs) {
            newAdminCourseStudentsData.totalDocs += 1;
          } else {
            newAdminCourseStudentsData.totalDocs = 1;
          }

          state.adminCourseStudentsData = newAdminCourseStudentsData;
        }
      })
      // Delete course student
      .addCase(deleteCourseStudent.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        if (state.adminCourseStudentsData) {
          const newAdminCourseStudentsData = {
            ...state.adminCourseStudentsData,
          };

          newAdminCourseStudentsData.list = newAdminCourseStudentsData.list.filter(
            (item) => item._id !== result._id
          );

          if (newAdminCourseStudentsData.totalDocs) {
            newAdminCourseStudentsData.totalDocs -= 1;
          }

          state.adminCourseStudentsData = newAdminCourseStudentsData;
        }
      })
      // Update course tutor
      .addCase(updateCourseStudent.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        if (state.adminCourseStudentsData) {
          const newAdminCourseStudentsData = {
            ...state.adminCourseStudentsData,
          };

          const index = newAdminCourseStudentsData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminCourseStudentsData.list[index] = result;
          }

          state.adminCourseStudentsData = newAdminCourseStudentsData;
        }
      });
    // Matcher
    builder
      .addMatcher(
        isPending(
          getCourseStudentsAsAdmin,
          createCourseStudent,
          deleteCourseStudent,
          updateCourseStudent
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isRejected(
          getCourseStudentsAsAdmin,
          createCourseStudent,
          deleteCourseStudent,
          updateCourseStudent
        ),
        (state) => {
          state.status = 'rejected';
        }
      );
  },
});

export const { resetAdminCourseStudentsData } = CourseStudentsSlice.actions;

export const getCourseStudentsState = (
  state: RootState
): RootState['courseStudents'] => state.courseStudents;

export * from './adminApi';
export default CourseStudentsSlice.reducer;
